import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import "./AccountPage.css";

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profilePic, setProfilePic] = useState(user?.photoURL || "");
  const [name, setName] = useState(user?.email?.split("@")[0] || "User");
  const [savedAddress, setSavedAddress] = useState("");
  const [orders, setOrders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [ticketForm, setTicketForm] = useState({ issueType: "", description: "" });
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticketImages, setTicketImages] = useState([]);
  const [ticketStatus, setTicketStatus] = useState({});
  const [selectedAddress, setSelectedAddress] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [showWallet, setShowWallet] = useState(false);
  const [coinAmount, setCoinAmount] = useState(0);

  // Address options (same as in Home.js)
  const addressOptions = [
    "Cama Park , Mumbai",
    "Sky Heights , Andheri west",
    "Vrindawan colony , Andheri East ",
    "Radhakrishana Apartment, Andheri west",
    "Akme Paradise, Ville Parle"
  ];

  useEffect(() => {
    fetchUserData();
    fetchOrders();
    fetchWalletBalance();
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;
    
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      setProfilePic(data.profilePic || "");
      setSavedAddress(data.address || "");
    } else {
      await setDoc(userRef, { profilePic: "", address: "", orders: [] });
    }
  };

  const fetchOrders = async () => {
    if (!user) return;

    try {
      const ordersCollection = collection(db, "users", user.uid, "orders");
      const ordersSnapshot = await getDocs(ordersCollection);
      const orderList = ordersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(orderList);

      orderList.forEach((order) => {
        const ticketRef = doc(db, "tickets", order.id);
        onSnapshot(ticketRef, (ticketSnap) => {
          if (ticketSnap.exists()) {
            setTicketStatus((prev) => ({
              ...prev,
              [order.id]: ticketSnap.data().status,
            }));
          }
        });
      });
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const handleProfileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      setProfilePic(reader.result);
      setUploading(false);

      if (user) {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { profilePic: reader.result });
      }
    };
  };

  const handleAddressUpdate = async () => {
    if (!user || !selectedAddress) return;

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, { address: selectedAddress });
    setSavedAddress(selectedAddress);
    setShowAddressForm(false);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + ticketImages.length > 6) {
      alert("You can upload a maximum of 6 images.");
      return;
    }

    const promises = files.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    });

    Promise.all(promises)
      .then((base64Images) => {
        setTicketImages((prevImages) => [...prevImages, ...base64Images]);
      })
      .catch((error) => console.error("Error uploading images:", error));
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  

  const fetchWalletBalance = async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      setWalletBalance(data.walletBalance || 0);
    }
  };

  const handleBuyCoins = async () => {
    if (coinAmount <= 0) {
      alert("Please enter a valid coin amount.");
      return;
    }
  
    const isRazorpayLoaded = await loadRazorpay();
    if (!isRazorpayLoaded) {
      alert("Failed to load Razorpay. Try again!");
      return;
    }
  
    const amountInPaise = coinAmount * 100; // Razorpay works in paise
  
    const options = {
      key: "rzp_test_IhWWq82FB3veeK", // Replace with your Razorpay key
      amount: amountInPaise,
      currency: "INR",
      name: "Coin Purchase",
      description: "Buying coins for wallet",
      handler: async function (response) {
        alert(`Payment Successful! Payment ID: ${response.razorpay_payment_id}`);
        const newBalance = walletBalance + coinAmount;
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { walletBalance: newBalance });
        setWalletBalance(newBalance);
        setCoinAmount(0);
      },
      prefill: {
        name: user.displayName || "Customer",
        email: user.email,
        contact: "9876543210", // Optional: User's contact number
      },
      theme: {
        color: "#F37254",
      },
    };
  
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  

  
  

  const handleRaiseTicket = async (orderId) => {
    if (!ticketForm.issueType || !ticketForm.description) {
      alert("Please fill all fields!");
      return;
    }

    setSubmitting(true);

    try {
      await setDoc(doc(db, "tickets", orderId), {
        userId: user.uid,
        orderId: orderId,
        issueType: ticketForm.issueType,
        description: ticketForm.description,
        images: ticketImages,
        status: "Open",
        timestamp: new Date(),
      });

      alert("Ticket raised successfully!");
      setShowTicketForm(false);
      setTicketForm({ issueType: "", description: "" });
      setTicketImages([]);
    } catch (error) {
      console.error("Error submitting ticket:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="account-container">
      <div className="account-card">
        <h2 className="account-title">Hello, {name}!</h2>

        <div className="profile-section" onClick={() => document.getElementById("profile-upload").click()}>
          <img
            className="profile-image"
            src={profilePic || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
            alt="Profile"
          />
        </div>
        <input id="profile-upload" type="file" accept="image/*" onChange={handleProfileUpload} style={{ display: "none" }} />
        {uploading && <p className="uploading-text">Uploading...</p>}

        {savedAddress && <p className="saved-address">{savedAddress}</p>}
        <button className="add-address-btn" onClick={() => setShowAddressForm(!showAddressForm)}>
          + Add Address
        </button>

        {showAddressForm && (
          <div className="address-form-container">
            <select
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              className="address-dropdown"
            >
              <option value="">Select Address</option>
              {addressOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button className="update-btn" onClick={handleAddressUpdate}>Save Address</button>
          </div>
        )}
<div className="collapsible-card">
        <div className="card-header" onClick={() => setShowWallet(!showWallet)}>
          <h3>Wallet {showWallet ? '▲' : '▼'}</h3>
        </div>
        {showWallet && (
          <div className="wallet-section">
            <h4>Wallet Balance: ₹{walletBalance}</h4>
            <div className="coins">
              <img src="https://cdn-icons-png.flaticon.com/512/1286/1286100.png" alt="Coin" />
              <img src="https://cdn-icons-png.flaticon.com/512/1286/1286100.png" alt="Coin" />
              <img src="https://cdn-icons-png.flaticon.com/512/1286/1286100.png" alt="Coin" />
            </div>
            <input
              type="number"
              value={coinAmount}
              onChange={(e) => setCoinAmount(Number(e.target.value))}
              placeholder="Enter amount"
            />
            <button onClick={handleBuyCoins}>Buy Coins</button>
          </div>
        )}
      </div>

   




        <div className="collapsible-card" onClick={() => setShowOrders(!showOrders)}>
          <h3>Your Orders {showOrders ? "▲" : "▼"}</h3>
          {showOrders && (
            <div className="order-list">
              {orders.length === 0 ? (
                <p>No orders found!</p>
              ) : (
                orders.map((order, index) => (
                  <div key={order.id} className="order-card">
                    <h4>Order ID: {order.id}</h4>
                    <p>Order Type: {order.orderType}</p>
                    {order.orderType==="schedule"&& order.selectedSlot &&(
                      <p>Scheduled Slot:{order.selectedSlot}</p>
                    )}
                    <p>Payment Method: {order.paymentMethod}</p>
                    <p>Payment Status: {order.paymentStatus}</p>
                    <p>Delivery Date: {order.deliveryDate}</p>
                    <p>Status: {order.status}</p>
                    <h4>Items:</h4>
                    <ul>
                      {order.items.map((item, index) => (
                        <li key={index}>
                          {item.name} - ₹{item.price} x {item.quantity} = ₹{item.totalPrice}
                        </li>
                      ))}
                    </ul>
                    <h4>Total Amount: ₹{order.totalAmount}</h4>
                    {index === 0 && (
                      <button
                        className={`raise-ticket-btn ${ticketStatus[order.id] === "Resolved" ? "resolved" : "raised"}`}
                        onClick={() => setShowTicketForm(true)}
                        disabled={ticketStatus[order.id] === "Resolved"}
                      >
                        {ticketStatus[order.id] === "Resolved"
                          ? "Resolved"
                          : ticketStatus[order.id] === "Open"
                          ? "Ticket Raised"
                          : "Raise a Ticket"}
                      </button>

                    )}

                    
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {showTicketForm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Raise a Ticket</h3>
              <select value={ticketForm.issueType} onChange={(e) => setTicketForm({ ...ticketForm, issueType: e.target.value })}>
                <option value="">Select Issue Type</option>
                <option value="Damaged Product">Damaged Product</option>
                <option value="Wrong Item">Wrong Item</option>
                <option value="Expired Item">Expired Item</option>
              </select>
              <textarea placeholder="Describe the issue..." value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}></textarea>
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
              <button onClick={() => handleRaiseTicket(orders[0].id)} disabled={submitting}>{submitting ? "Submitting..." : "Submit Ticket"}</button>
              <button onClick={() => setShowTicketForm(false)}>Close</button>
            </div>
          </div>
        )}

        <button className="logout-button" onClick={logout}>Logout</button>
      </div>
    </div>
  );
};

export default AccountPage;
